<template>
    <!-- listing -->
    <div class="widget">
        <div>
            <div class="listItem is-small mb-2" v-if="effectiveDocuments?.length == 0">None Provided</div>
            <div class="listItem mb-2" v-for="document in effectiveDocuments" :key="document.id">
                <span class="tag is-white is-rounded is-medium has-text-weight-bold">
                    <a @click="viewDocument(document)">
                        <i class="fas fa-paperclip mr-3"></i>
                        <span class="listFilename">{{ document.filename }}</span>
                    </a>
                </span>
                
                <a v-if="!readonly" @click="documentToDelete = document" class="ml-3 mr-3">
                    <i class="fas fa-trash-alt"></i>
                </a>
            </div>
        </div>

        <!-- file upload -->
        <div v-if="!readonly" class="file has-name is-link is-small pt-2">
            <label class="file-label">
                <input class="file-input" type="file" name="file" :disabled="uploadInProgress" :multiple="documents !== undefined" @change="uploadDocuments(($event.target as HTMLInputElement).files!)"/>
                <span class="file-cta">
                    <span class="file-icon">
                        <i class="fas fa-upload"></i>
                    </span>
                    <span class="file-label">{{ uploadInProgress ? "Uploading…" : (singleDocument ? "Replace File…" : "Attach File…") }}</span>
                </span>
                <span v-if="filename" class="file-name">{{ filename }}</span>
            </label>
        </div>
    </div>

    <!-- delete confirm modal -->
    <ModalDialog
        :active="documentToDelete != null"
        title="Confirm Delete"
        :allow-close="true"
        @close="documentToDelete = null"
        first-button-text="Go Back"
        first-button-severity="link"
        @firstbuttonselected="documentToDelete = null"
        second-button-text="Delete"
        second-button-severity="danger"
        @secondbuttonselected="deleteDocument(documentToDelete!)"
    >
        <p>
            <strong>Delete {{ documentToDelete?.filename }}?</strong>
        </p>
    </ModalDialog>

</template>

<script setup lang="ts">
import { Core } from "@minicity/core";
import { ref, computed } from "vue";
import { notifyError } from "@/core/utils";

const props = defineProps<{
    documents?: Core.Document[];
    singleDocument?: Core.Document|null;
    readonly?: boolean;
}>();

const documentToDelete = ref<Core.Document | null>(null);
const uploadInProgress = ref<boolean>(false);

const effectiveDocuments = computed(()=>{
    if (props.documents) return props.documents;
    return props.singleDocument ? [props.singleDocument] : [];
})

const emit = defineEmits<{
    (e: 'delete', value: Core.Document): void,
    (e: 'view', value: Core.Document): void,
    (e: 'upload', value: {file: File, onDone: ()=>any}): void,
}>();

const filename = ref<string|null>(null);

const deleteDocument = (doc: Core.Document) => {
    documentToDelete.value = null;
    emit('delete', doc);
}

const viewDocument = (doc: Core.Document) => {
    emit('view', doc);
}

const uploadDocuments = (files: FileList) => {
    let i=0;

    const uploadNextDocument = () => {
        filename.value = `(${i+1}/${files.length}) - ${files[i].name}`;
        uploadInProgress.value = true;

        emit('upload', {
            file: files[i],
            onDone: () => { 
                i++;

                if (i < files.length) {
                    // There are more files
                    if (props.documents != null) {
                        // This is a multi-document upload widget, so we should upload the next file
                        uploadNextDocument();
                    } else {
                        // This is a single-document upload widget, so only one file can be uploaded
                        notifyError("Only one file may be uploaded here");
                    }
                } else {
                    // We're done
                    filename.value = null;
                    uploadInProgress.value = false;
                }
            }
        });
    }

    uploadNextDocument();
}

</script>

<style lang="scss" scoped>
.widget {
    border-style: solid;
    border-color: rgb(0,0,0,0.07);
    border-width: 1px;
    background: rgb(0,0,0,0.06);
    padding: 1em;
    border-radius: 0.25em;
}

.listFilename {
    display: inline-block;
    max-width: 500px;
    overflow: hidden;
    text-overflow: ellipsis;

    vertical-align: bottom;
}

.file-name {
    background: white;
}
</style>